<template>
  <div>
    <div v-if="!working">
      <b-alert variant="info" :show="shippingsWithErrors.length > 0">
        <p v-translate="{count: shippingsWithErrors.length}">FYI, there are %{count} shippings with errors (address, status, already in a tour and/or not in routing zone) that won't be routed by this tool.</p>
        <b-button size="sm" @click.prevent="copyToClipboard(shippingsWithErrors.join('\n'))" v-translate>Copy IDs to clipboard</b-button>
      </b-alert>
      <b-card>
          <b-row>
            <b-col cols="4">
              <translate>Total Shippings Fetched</translate>
              <h1 class="m-0">{{total}}</h1>
            </b-col>
            <b-col cols="4">
              <translate>Specific Provider</translate>
              <div v-if="!provider">
                <company-autocomplete ref="courierCompanyAc" @selected="onSearchCourierCompanySelected" :restrict-to="[['relationship', 'eq', 'provider']]" />
              </div>
              <b-card v-else>
                <span class="bg-danger text-white d-block text-center" style="cursor: pointer; width: 1rem; height: 1rem; line-height: 1rem; position: absolute; top: 0; left: 0" href="#" @click.prevent="removeCourierCompany">×</span>
                <company-card :company="provider" :profile-picture="false" :with-contact="false" />
              </b-card>
            </b-col>
            <b-col>
              <br>
              <b-button :disabled="!shippings.length" size="sm" variant="success" @click.prevent="checkForQuotes" v-translate>Check for Quotes and Rules</b-button>
            </b-col>
            <b-col cols="12">
              <hr>
            </b-col>
            <b-col cols="4">
              <translate>Valid Shippings to Outsource</translate>
              <h1 class="m-0">{{toOutsource.length}}</h1>
            </b-col>
            <b-col cols="4">
              <translate>Shipping Limit</translate>
              <b-input class="h1" v-model="shippingLimit" size="xl" type="number" />
            </b-col>
            <b-col>
              <br>
              <b-button :disabled="toOutsource.length === 0" size="sm" variant="success" @click.prevent="outsource" v-translate>Outsource Shippings</b-button>
            </b-col>
          </b-row>
          <b-row v-for="(group, index) in shippingGroups" :key="index">
            <b-col><b-form-checkbox v-model="selected" :value="{[index]: group}">{{ index }}</b-form-checkbox></b-col>
            <b-col>
              <span :id="'tooltip-target-' + index">{{ group.length }}</span>
              <b-tooltip :target="'tooltip-target-' + index" triggers="hover">
                <div v-for="(item, index) in clusterShippingSizes(group)" :key="index">
                  <b-badge class="mr-1" variant="dark">{{index}}</b-badge>
                  <span
                    v-translate="{count: item}"
                    :translate-n="item"
                    translate-plural="%{ count } shippings"
                  >%{ count } shipping
                  </span>
                </div>
                <p class="m-0 mt-2">{{clusterShippingsWeight(group)}} KG.</p>
              </b-tooltip>
            </b-col>
            <b-col>
              <b-input-group>
                <b-input size="sm" type="number" number v-model="toRoute[index]" class="mr-1" />
                <b-button size="sm" :variant="assignButtonBg(index)" v-b-modal.modalAssignCouriers @click="assigningGroup = index" :disabled="asWaiting[index] || allAsWaiting">{{ toAssign[index] ? toAssign[index].length : 0 }} <font-awesome-icon icon="user" /></b-button>
              </b-input-group>
            </b-col>
            <b-col><b-select size="sm" v-if="clusteringMethod[index]" v-model="clusteringMethod[index]" :options="clusteringMethodSelect"></b-select></b-col>
            <b-col>
              <b-button size="sm" @click="viewGroupMap(index)"><font-awesome-icon :icon="['far', 'eye']" /></b-button>
              <b-form-checkbox class="d-inline-block ml-1" style="line-height: 1" switch size="sm" v-model="asWaiting[index]" :disabled="allAsWaiting || toAssign[index] && toAssign[index].length > 0"><translate>as WAITING</translate></b-form-checkbox>
            </b-col>
            <b-col cols="12"><hr class="my-2"></b-col>
          </b-row>
      </b-card>
    </div>
    <b-card v-else>
      <b-row v-if="!posting && !checking">
        <b-col class="text-center my-5">
          <h2>
            <b-spinner  />
            <translate>Fetching Shippings, please wait...</translate>
          </h2>
          <h3 v-show="total !== 0" v-translate="{count: count < total ? count : total, total}">%{count}/%{total}</h3>
          <h3 v-show="total === 0" v-translate>Calculating total shippings to fetch...</h3>
          <h4 v-show="this.remainingTime !== 0" v-translate="{remainingTime: this.remainingTime}">Ending %{remainingTime}</h4>
          <h4 v-show="this.remainingTime === 0" v-translate>Calculating remaining time...</h4>
        </b-col>
      </b-row>
      <b-row v-if="!posting && checking">
        <b-col class="text-center my-5">
          <h2>
            <b-spinner  />
            <translate>Checking for quotes and rules, please wait...</translate>
          </h2>
          <h3 v-show="total !== 0" v-translate="{count: count < total ? count : total, total}">%{count}/%{total}</h3>
          <h3 v-show="total === 0" v-translate>Calculating total to check...</h3>
          <h4 v-show="this.remainingTime !== 0" v-translate="{remainingTime: this.remainingTime}">Ending %{remainingTime}</h4>
          <h4 v-show="this.remainingTime === 0" v-translate>Calculating remaining time...</h4>
        </b-col>
      </b-row>
      <b-row v-if="posting && !success">
        <b-col class="text-center my-5">
          <h2>
            <b-spinner  />
            <translate>Outsourcing Shippings, please wait...</translate>
          </h2>
          <h3 v-show="total !== 0" v-translate="{count: count, total}">%{count}/%{total}</h3>
          <h3 v-show="total === 0" v-translate>Calculating total shippings to outsource...</h3>
          <h4 v-show="this.remainingTime !== 0" v-translate="{remainingTime: this.remainingTime}">Ending %{remainingTime}</h4>
          <h4 v-show="this.remainingTime === 0" v-translate>Calculating remaining time...</h4>
        </b-col>
      </b-row>
      <b-row v-else-if="success">
        <b-col class="text-center my-5">
          <h1>
            <translate>All Shippings have been outsourced! 😊</translate>
          </h1>
          <h2 v-show="total !== 0" v-translate="{total}">%{total}/%{total}</h2>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import ShippingsMixins from '@/mixins/shippings.mixin.js'
import CompanyAutocomplete from '@/components/autocompletes/CompanyAutocomplete'
import CompanyCard from '@/components/CompanyCard'

export default {
  name: 'ShippingBulk3p',
  components: {
    CompanyAutocomplete,
    CompanyCard
  },
  props: {
    filters: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      working: false,
      checking: false,
      posting: false,
      success: false,
      fetchChunkSize: 300,
      checkChunkSize: 30,
      postChunkSize: 3,
      counter: 0,
      estimatedChunks: 0,
      remainingTime: 0,
      timeDiff: [],
      shippings: [],
      shippingGroups: {},
      shippingsWithErrors: [],
      toOutsource: [],
      shippingLimit: null,
      provider: null,
      selected: [],
      divider: 25,
      toRoute: {},
      toRouteTotal: 0,
      asWaiting: {},
      countCheck: 0,
      count: 0,
      total: 0
    }
  },
  mixins: [ShippingsMixins],
  mounted() {
    delete this.filters.format
    delete this.filters.includes
    this.filters.limit = this.fetchChunkSize
    this.filters.page = 0
    this.filters.fields = 'id,from_area_id,to_area_id,last_known_area_id,company_id,shipping_type_id,size_id'
    this.getShippings()
  },
  methods: {
    async getShippings() {
      this.timeDiff = []
      this.working = true
      await this.getChunk(this.filters)
        .then(async () => {
          this.working = false
        })
    },
    async getChunk(params, prevResults = []) {
      const startTime = new Date()
      let chunkResults = []
      return await this.$http.get(this.$appConfig.api.baseUrl + 'shippings', {params})
        .then(async response => {
          chunkResults = prevResults.concat(response.body.data)
          const endTime = new Date()
          this.timeDiff.push(endTime - startTime)
          this.total = response.body.pagination.total
          this.estimatedChunks = Math.ceil(this.total / this.fetchChunkSize)
          this.remainingTime = this.moment().add(((this.estimatedChunks - params.page) * (this.timeDiff.reduce((a, b) => a + b, 0) / this.timeDiff.length)), 'milliseconds').fromNow()
          this.animateValue(chunkResults.length, (chunkResults.length + this.fetchChunkSize) <= this.total ? chunkResults.length + this.fetchChunkSize : this.total, (this.timeDiff.reduce((a, b) => a + b, 0) / this.timeDiff.length))
          if (response.body.data.length !== 0) {
            params.page = params.page + 1
            await this.getChunk(params, chunkResults)
          } else {
            this.total = chunkResults.length
            this.shippings = chunkResults
          }
        }, async () => {
          this.$root.$children[0].$refs.simplert.justCloseSimplert()
          await this.getChunk(params, prevResults)
        })
    },
    async checkForQuotes() {
      this.checking = true
      this.working = true
      this.count = 0
      this.total = this.shippings.length
      const chunks = this.shippings.reduce((acc, curr, i) => {
        if (!(i % this.checkChunkSize)) {
          acc.push(this.shippings.slice(i, i + this.checkChunkSize))
        }
        return acc
      }, [])
      this.toOutsource = []
      let i = 0
      for (const chunk of chunks) {
        i++
        const startTime = new Date()
        this.toOutsource = this.toOutsource.concat(await this.doCheckChunk(chunk))
        const endTime = new Date()
        this.timeDiff.push(endTime - startTime)
        this.countCheck += chunk.length
        this.estimatedChunks = chunks.length
        this.remainingTime = this.moment().add(((chunks.length - i) * (this.timeDiff.reduce((a, b) => a + b, 0) / this.timeDiff.length)), 'milliseconds').fromNow()
        this.animateValue(i * this.checkChunkSize, (i * this.checkChunkSize + this.checkChunkSize) < this.shippings.length ? i * this.checkChunkSize + this.checkChunkSize : this.shippings.length, (this.timeDiff.reduce((a, b) => a + b, 0) / this.timeDiff.length - 200))
      }
      this.toOutsource = this.toOutsource.filter(Boolean)
      this.checking = false
      this.working = false
    },
    async doCheckChunk(chunk) {
      let promises = []
      for (let shipping of chunk) {
        let shippingToCheck = shipping
        if (this.provider?.id) {
          shippingToCheck = {...shippingToCheck, company_id_provider: this.provider.id}
        }
        promises.push(this.check3pQuote(shippingToCheck, true))
      }
      return await Promise.all(promises)
    },
    async outsource() {
      let toPost = this.toOutsource
      if (this.shippingLimit && parseInt(this.shippingLimit) > 0) {
        toPost = toPost.slice(0, parseInt(this.shippingLimit))
      }

      this.posting = true
      this.working = true
      this.total = toPost.length
      this.count = 0
      this.timeDiff = []
      const chunks = toPost.reduce((acc, curr, i) => {
        if (!(i % this.postChunkSize)) {
          acc.push(toPost.slice(i, i + this.postChunkSize))
        }
        return acc
      }, [])
      let i = 0
      for (const chunk of chunks) {
        i++
        const startTime = new Date()
        this.toOutsource = this.toOutsource.concat(await this.doOutsourceChunk(chunk))
        const endTime = new Date()
        this.timeDiff.push(endTime - startTime)
        this.countCheck += chunk.length
        this.estimatedChunks = chunks.length
        this.remainingTime = this.moment().add(((chunks.length - i) * (this.timeDiff.reduce((a, b) => a + b, 0) / this.timeDiff.length)), 'milliseconds').fromNow()
        this.animateValue(i * this.postChunkSize, (i * this.postChunkSize + this.postChunkSize) < this.toOutsource.length ? i * this.postChunkSize + this.postChunkSize : this.toOutsource.length, (this.timeDiff.reduce((a, b) => a + b, 0) / this.timeDiff.length - 200))
      }
      this.shippings = this.toOutsource.filter(Boolean)
      this.checking = false
      this.success = true
      setTimeout(() => {
        this.$root.$emit('bv::refresh::table', 'my-table')
      }, 100)
    },
    async doOutsourceChunk(chunk) {
      let promises = []
      for (const shipping of chunk) {
        promises.push(this.sendTo3p(shipping))
      }
      return await Promise.all(promises)
    },
    onSearchCourierCompanySelected(item) {
      this.provider = item
      this.$refs.courierCompanyAc.searchQuery = ''
    },
    removeCourierCompany() {
      this.provider = null
    },
    animateValue(start, end, duration) {
      let startTimestamp = null
      const step = (timestamp) => {
        if (!startTimestamp) startTimestamp = timestamp
        const progress = Math.min((timestamp - startTimestamp) / duration, 1)
        this.count = this.count < this.total ? Math.floor(progress * (end - start) + start) : this.total
        if (progress < 1) {
          window.requestAnimationFrame(step)
        }
      }
      window.requestAnimationFrame(step)
    }
  }
}
</script>
